/**
 * Helper function to check subscription status for route guards
 * This function will use cached subscription data if available,
 * only fetching from API if data hasn't been loaded yet or if
 * the requested account differs from the cached one
 *
 * @param {Object} store - Vuex store instance
 * @param {String} accountId - Account ID from route params
 * @returns {Promise<Boolean>} - Returns true if subscription is active/trialing
 */
export async function checkSubscriptionStatus(store, accountId) {
  // stripe/fetchedAccountId is always stored as a Number (see
  // stripe.js#fetchSubscription), but route params (accountId here) are
  // always Strings — normalize before comparing or the cache guard never hits.
  const numericAccountId = Number(accountId);

  // Check if subscription data has been fetched
  const isFetched = store.getters['stripe/isSubscriptionFetched'];
  const fetchedAccountId = store.getters['stripe/getFetchedAccountId'];
  const status = store.getters['stripe/getSubscriptionStatus'];

  // Fetch if data hasn't been loaded yet, or if requesting a different account.
  // A null status (e.g. a 404 at boot, before the signup trial's webhook wrote
  // the entitlement row) must force: fetchSubscription otherwise short-circuits
  // on the same fetchedAccountId and the stale null bounces to pricing_table.
  if (!isFetched || status === null || fetchedAccountId !== numericAccountId) {
    await store.dispatch('stripe/fetchSubscription', {
      id: numericAccountId,
      force: fetchedAccountId !== numericAccountId || status === null,
    });
  }

  // Check if subscription is active or trialing
  const currentStatus = store.getters['stripe/getSubscriptionStatus'];
  return ['active', 'trialing'].includes(currentStatus);
}

/**
 * Resolves subscription access for a route guard, distinguishing a
 * confirmed "not active" status from a failure to determine status at all
 * (e.g. the subscription endpoint returning a 5xx), so callers can route
 * each case to a different page instead of collapsing both into one.
 *
 * @param {Object} store - Vuex store instance
 * @param {String} accountId - Account ID from route params
 * @returns {Promise<{hasActiveSubscription: Boolean, hasError: Boolean}>}
 */
export async function resolveSubscriptionAccess(store, accountId) {
  try {
    const hasActiveSubscription = await checkSubscriptionStatus(
      store,
      accountId
    );
    return { hasActiveSubscription, hasError: false };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to determine subscription status', error);
    return { hasActiveSubscription: false, hasError: true };
  }
}

const HIGH_TIERS = ['pro', 'enterprise'];

/**
 * Sync tier check for Vue computed/render usage, where the dashboard shell
 * has already triggered fetchSubscription (App.vue does this on account
 * init) before these consumers render. Reactive: once that fetch resolves,
 * getSubscriptionTier updates and any computed reading this re-evaluates —
 * so a null/unfetched tier only shows as "restricted" for a frame, not
 * permanently.
 *
 * @param {Object} store - Vuex store instance
 * @returns {Boolean} - True if the account's tier is pro or enterprise
 */
export function hasHighTierAccess(store) {
  return HIGH_TIERS.includes(store.getters['stripe/getSubscriptionTier']);
}

/**
 * Async tier check for route guards and anywhere else that can't rely on
 * reactivity to self-correct — guarantees the subscription fetch has
 * completed (or triggers it) before reading tier, using the same
 * cache/fetch semantics as checkSubscriptionStatus. Without this, a guard
 * that reads getSubscriptionTier directly can race the app-load fetch and
 * bounce an entitled user before their tier has loaded.
 *
 * Mirrors resolveSubscriptionAccess's error handling: fetchSubscription
 * rethrows non-404 failures, so a caller that doesn't catch here would see
 * its own promise reject and — since router beforeEnter guards are only
 * resolved via calling next(), not via their returned promise — never call
 * next() at all, hanging navigation on a blank page.
 *
 * @param {Object} store - Vuex store instance
 * @param {String} accountId - Account ID from route params
 * @returns {Promise<{hasAccess: Boolean, hasError: Boolean}>}
 */
export async function resolveTierAccess(store, accountId) {
  try {
    await checkSubscriptionStatus(store, accountId);
    return { hasAccess: hasHighTierAccess(store), hasError: false };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to determine tier access', error);
    return { hasAccess: false, hasError: true };
  }
}

// Basic plan does get AI Studio access — only Starter doesn't — so this
// can't reuse HIGH_TIERS above, which gates the deliberately broader set of
// pro+-only features (organization module, widget-builder branding, etc.).
function isStarterPlan(planName) {
  return /^starter plan$/i.test((planName || '').trim());
}

/**
 * AI-Studio-specific access check (sidebar "Open AI Studio" button, MHub's
 * "Open AI Studio Secrets" button, chatbot config's Advanced Mode toggle).
 * Falls back to the pro/enterprise-only rule when plan_name isn't available
 * (legacy responses, Cypress fixtures) — Starter can't be distinguished from
 * Basic by inbox count alone, so an unknown plan name fails closed the same
 * way hasHighTierAccess already does.
 *
 * @param {Object} store - Vuex store instance
 * @returns {Boolean} - True unless the account is on the Starter plan
 */
export function hasAiStudioAccess(store) {
  const planName = store.getters['stripe/getPlanName'];
  if (!planName) return hasHighTierAccess(store);
  return !isStarterPlan(planName);
}

/**
 * Reusable route guard for subscription-protected routes
 *
 * @param {Object} to - Route object being navigated to
 * @param {Object} from - Route object being navigated from
 * @param {Function} next - Navigation callback
 * @param {Object} store - Vuex store instance
 */
export async function subscriptionGuard(to, from, next, store) {
  // Cypress E2E specs stub the external knowledge/subscription service
  // entirely via cy.intercept; skip the real check when running under
  // Cypress in the test build specifically. CYPRESS_TEST_BUILD is baked in
  // at webpack build time (see Procfile.test) via webpacker's
  // EnvironmentPlugin, so this can never evaluate true in a production
  // bundle no matter what a user's browser sets on window.Cypress. (Not
  // gated on NODE_ENV: this project's config/webpack/test.js always
  // defaults NODE_ENV to 'development' regardless of RAILS_ENV/environment,
  // so it can't distinguish a test build from a real dev build.)
  //
  // A spec that needs the real check (the subscription redirect specs) opts
  // back in by setting window.e2eEnforceSubscriptionGuard before each page
  // load — see cy.enforceSubscriptionGuard(). It is only ever read after the
  // env check above, so it can't matter outside the test build.
  if (
    process.env.CYPRESS_TEST_BUILD === 'true' &&
    window.Cypress &&
    !window.e2eEnforceSubscriptionGuard
  ) {
    return next();
  }

  const accountId = to.params.accountId;
  const { hasActiveSubscription, hasError } = await resolveSubscriptionAccess(
    store,
    accountId
  );

  if (hasError) {
    return next({
      name: 'subscription_error',
      params: { accountId },
      query: { redirect: to.fullPath },
    });
  }

  if (!hasActiveSubscription) {
    return next({ name: 'pricing_table', params: { accountId } });
  }

  return next();
}
